import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Route, RouteComponentProps } from 'react-router';
import { renderRoutes } from 'react-router-config';
import { Url, USER_PROFILE_POSTS_SUB_PATHS } from '@wix/da-url';
import HiddenIcon from '@wix/da-shared-react/pkg/Icons/Hidden';
import Separator from '@wix/da-shared-react/pkg/Separator';
import SubSubNavBar, { SubSubNavBarItem } from '../../SubSubNavBar';
import { PostsSortOrder } from '../../../../types/posts';

import PostsAll from './PostsAll';
import Journals from './Journals';
import Statuses from './Statuses';
import Polls from './Polls';
import Drafts from './Drafts';
import SectionViewTypeSwitch from '../../SectionViewSwitch';
import { SectionViewType } from '../../../../types/sections';

import { Breakpoint } from '@wix/da-shared-react/pkg/Separator/Separator';
import s from './PostsSection.scss';

export interface Props {
  isOwner: boolean;
  ownerUsername: string;
  sortOrder?: PostsSortOrder;
  total: {
    all: number;
    journals: number;
    polls: number;
    statuses: number;
    drafts: number;
  };
}

interface RouteParams {
  sortOrder?: PostsSortOrder;
}

export interface State {
  viewType: SectionViewType;
}

class PostsSection extends React.Component<
  Props & WithTranslation & RouteComponentProps<RouteParams>,
  State
> {
  state = {
    viewType: SectionViewType.LIST,
  };
  render() {
    const { ownerUsername, sortOrder } = this.props;
    const { viewType } = this.state;
    return (
      <div className={s['root']}>
        <SubSubNavBar
          className={s['section-nav']}
          rootClassName={s['subnav-root']}
          iconClassName={s['subnav-icon']}
          items={this.getPostsRoutes()}
          extras={
            <Route
              key="nav-user"
              path={Url.userPostsPath({
                username: ownerUsername,
                subPath: USER_PROFILE_POSTS_SUB_PATHS.JOURNALS,
              })}
              render={() => (
                <SectionViewTypeSwitch
                  className={s['section-view-switch']}
                  value={viewType}
                  onChange={this.onViewTypeChange}
                />
              )}
            />
          }
        />
        <Separator
          className={s['separator']}
          forBreakpoint={Breakpoint.Mobile}
        />
        {renderRoutes(this.getPostsRoutes(), {
          viewType,
          sortOrder,
          onSortOrderChange: this.onSortOrderChange,
        })}
      </div>
    );
  }

  getPostsRoutes = () => {
    const t = this.props.t!;
    const { isOwner, ownerUsername, total } = this.props;
    const username = ownerUsername.toLowerCase();
    const routes: SubSubNavBarItem[] = [
      {
        label: t('common.all'),
        count: total.all,
        path: Url.userPostsPath({
          username,
          subPath: USER_PROFILE_POSTS_SUB_PATHS.ALL,
        }),
        component: PostsAll,
        exact: true,
        bi_item_label: 'all',
      },
      {
        label: t('common.journals_plural'),
        count: total.journals,
        path: Url.userPostsPath({
          username,
          subPath: USER_PROFILE_POSTS_SUB_PATHS.JOURNALS,
        }),
        component: Journals,
        bi_item_label: USER_PROFILE_POSTS_SUB_PATHS.JOURNALS,
      },
      {
        label: t('common.statuses_plural'),
        count: total.statuses,
        path: Url.userPostsPath({
          username,
          subPath: USER_PROFILE_POSTS_SUB_PATHS.STATUSES,
        }),
        component: Statuses,
        bi_item_label: USER_PROFILE_POSTS_SUB_PATHS.STATUSES,
      },
      {
        label: t('common.polls_plural'),
        count: total.polls,
        path: Url.userPostsPath({
          username,
          subPath: USER_PROFILE_POSTS_SUB_PATHS.POLLS,
        }),
        component: Polls,
        bi_item_label: USER_PROFILE_POSTS_SUB_PATHS.POLLS,
      },
    ];
    if (isOwner) {
      routes.push({
        label: t('common.drafts_plural'),
        labelIconClass: HiddenIcon,
        tooltipText: t('navigation.subnavigation.draftsVisibility'),
        count: total.drafts,
        path: Url.userPostsPath({
          username,
          subPath: USER_PROFILE_POSTS_SUB_PATHS.DRAFTS,
        }),
        component: Drafts,
        bi_item_label: USER_PROFILE_POSTS_SUB_PATHS.DRAFTS,
      });
    }
    return routes;
  };

  onViewTypeChange = viewType => {
    this.setState({ viewType });
  };

  onSortOrderChange = (
    subPath: USER_PROFILE_POSTS_SUB_PATHS,
    sortOrder: PostsSortOrder
  ) => {
    const { history, ownerUsername } = this.props;
    const postsPath = Url.userPostsPath({
      username: ownerUsername,
      subPath,
      sortOrder,
    });
    history.push(postsPath);
  };
}

export default withTranslation()(PostsSection) as React.ComponentType<Props>;
